/**
 * Segment-click adapter for the Status Overview. Delegates the
 * attribute-agnostic single-select toggle to
 * `toggleSingleSelectFilterToken`; the validation-specific work here
 * is limited to building the correct chip shape per bar and scoping
 * the "select group" to chips belonging to the same bar.
 *
 * Two chip shapes coexist:
 *
 * - `validationStatus` compound chips (e.g. `LVS_PASSED`) — used by
 *   per-pipeline bars. Prefix identifies the bar.
 *
 * - Direct chips (e.g. `validationOverallStatus = PASSED`) — used by
 *   bars whose backing attribute stores the scalar status directly.
 *
 * Single-select is per-bar: clicking on the LVS bar only replaces
 * `LVS_*` chips and leaves any other bar's chip alone. This matches
 * the highlight behavior, where each bar tracks its own
 * `activeStatusValues` independently.
 */

import { filterPropertyFilterTree } from "../../searchSideBar/propertyFilterTree";
import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "../../searchSideBar/searchSideBarConnector";
import { toggleSingleSelectFilterToken } from "../../searchSideBar/singleSelectFilterToken";
import {
  filterChipForSegment,
  isBarChipPropertyKey,
  STATUS_ATTRIBUTE_SPECS,
  StatusSegment,
  VALIDATION_STATUS_ATTRIBUTE_KEY,
} from "./chartData";

/**
 * Returns a fresh `PropertyFilterGroup` with the clicked bar's status
 * chip added, replaced, or toggled off. `current` is never mutated.
 * Unknown `segment.attributeKey` values are a no-op.
 */
export function toggleStatusFilterToken(
  current: PropertyFilterGroup,
  segment: Pick<StatusSegment, "attributeKey" | "statusValue">
): PropertyFilterGroup {
  const spec = STATUS_ATTRIBUTE_SPECS.find(
    (s) => s.attributeKey === segment.attributeKey
  );
  if (spec === undefined) {
    return current;
  }
  const chip = filterChipForSegment(spec, segment.statusValue);
  if (chip === undefined) {
    return current;
  }

  const newFilter: PropertyFilter = {
    type: "PropertyFilter",
    operator: "filter",
    propertyKey: chip.propertyKey,
    value: chip.value,
  };

  // Strip only chips belonging to this bar so cross-bar single-select
  // stays independent: an LVS click doesn't wipe a HATTRICK chip.
  const isMemberOfThisBar = (f: PropertyFilter): boolean => {
    if (spec.filterChip.kind === "validationStatus") {
      return (
        f.propertyKey === VALIDATION_STATUS_ATTRIBUTE_KEY &&
        typeof f.value === "string" &&
        f.value.startsWith(`${spec.filterChip.prefix}_`)
      );
    }
    return f.propertyKey === spec.attributeKey;
  };

  return toggleSingleSelectFilterToken(current, newFilter, isMemberOfThisBar);
}

/**
 * Returns a fresh `PropertyFilterGroup` with every chip that could
 * originate from any Status Overview bar removed. `current` is never
 * mutated. Recurses fully through the tree, not only the first
 * nested group: legacy / shared URLs parse into multiple sibling
 * groups (e.g. one for the date range, one for `productLineId`, one
 * for `programId`) and `parsePropertyFilterFromQueryString` accepts
 * up to three levels of nesting, so bar chips can live at any depth.
 * Recursing here matches the highlight selector in `StatusOverview`
 * so the aggregation baseline stays in sync with what the bars mark
 * as active.
 *
 * Nested groups that empty out after stripping are dropped so the
 * tree doesn't accumulate empty PFGs.
 */
export function stripValidationStatusFilters(
  current: PropertyFilterGroup
): PropertyFilterGroup {
  return filterPropertyFilterTree(current, (f) =>
    isBarChipPropertyKey(f.propertyKey)
  );
}
